import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { subscribeToContacts, createContact, deleteContact, updateContact, findContactByName } from '../firebase/contacts'
import { subscribeToTags } from '../firebase/tags'
import ContactForm from './ContactForm'
import BulkImportModal from './BulkImportModal'
import './Contacts.css'

const Contacts = ({ user }) => {
  const navigate = useNavigate()
  const [contacts, setContacts] = useState([])
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [showBulkImport, setShowBulkImport] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedContacts, setSelectedContacts] = useState(new Set())
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, contactId: null, contactName: '', isBulk: false, bulkCount: 0 })
  const [tagConfirm, setTagConfirm] = useState({ show: false, tagName: '', contactCount: 0 })
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc') // 'asc' or 'desc'
  const [selectedTagValue, setSelectedTagValue] = useState('')
  const selectedContactsRef = useRef(selectedContacts)
  
  // Column widths with defaults
  const defaultColumnWidths = {
    select: 50,
    name: 200,
    company: 150,
    email: 200,
    phone: 150,
    tags: 180,
    linkedinStatus: 140,
    createdAt: 120,
    updatedAt: 120,
    actions: 80
  }
  
  const [columnWidths, setColumnWidths] = useState(() => {
    // Load from localStorage or use defaults
    const saved = localStorage.getItem('contacts-column-widths')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return { ...defaultColumnWidths, ...parsed }
      } catch (e) {
        console.error('Error parsing saved column widths:', e)
      }
    }
    return defaultColumnWidths
  })
  
  const [resizing, setResizing] = useState({ active: false, column: null, startX: 0, startWidth: 0 })
  const tableRef = useRef(null)

  useEffect(() => {
    if (!user) return

    // Subscribe to real-time contacts updates
    const unsubscribeContacts = subscribeToContacts(user.uid, (contactsData) => {
      setContacts(contactsData)
      setLoading(false)
    })

    // Subscribe to real-time tags updates
    const unsubscribeTags = subscribeToTags(user.uid, (tagsData) => {
      setTags(tagsData.map(tag => tag.name))
    })

    return () => {
      unsubscribeContacts()
      unsubscribeTags()
    }
  }, [user])

  const handleCreateContact = async (contactData) => {
    try {
      // Check for duplicate by name (case-insensitive)
      if (contactData.name && contactData.name.trim()) {
        const existingContact = await findContactByName(contactData.name, user.uid)
        
        if (existingContact) {
          // Duplicate found - show notification and automatically open existing contact
          alert(`⚠️ Duplicate Contact Found!\n\nA contact named "${contactData.name}" already exists. Opening the existing contact...`)
          setShowForm(false)
          navigate(`/contacts/${existingContact.id}`)
          return
        }
      }
      
      // No duplicate found, create new contact
      const contactId = await createContact(contactData, user.uid)
      setShowForm(false)
      // Navigate to the newly created contact's detail page
      // Small delay to ensure contact is saved before navigation
      setTimeout(() => {
        navigate(`/contacts/${contactId}`)
      }, 300)
    } catch (error) {
      console.error('Error creating contact:', error)
      alert('Failed to create contact. Please try again.')
    }
  }

  const handleDeleteContact = async (contactId, e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    
    console.log('Delete button clicked for contact:', contactId)
    
    // Find contact name for confirmation
    const contact = contacts.find(c => c.id === contactId)
    const contactName = contact?.name || 'this contact'
    
    // Show custom confirmation modal
    setDeleteConfirm({ show: true, contactId, contactName })
  }

  const confirmDelete = async () => {
    if (deleteConfirm.isBulk) {
      await confirmBulkDelete()
      return
    }

    const { contactId } = deleteConfirm
    
    console.log('User confirmed deletion, proceeding...')

    if (!user || !user.uid) {
      console.error('User not authenticated')
      alert('User not authenticated. Please refresh the page.')
      setDeleteConfirm({ show: false, contactId: null, contactName: '', isBulk: false, bulkCount: 0 })
      return
    }

    console.log('User authenticated, starting deletion process...')

    try {
      console.log('Calling deleteContact with:', { contactId, userId: user.uid })
      await deleteContact(contactId, user.uid)
      console.log('deleteContact completed successfully')
      setDeleteConfirm({ show: false, contactId: null, contactName: '', isBulk: false, bulkCount: 0 })
      alert('✅ Contact deleted successfully')
    } catch (error) {
      console.error('Error deleting contact:', error)
      console.error('Error name:', error.name)
      console.error('Error code:', error.code)
      console.error('Error message:', error.message)
      console.error('Error stack:', error.stack)
      alert(`❌ Failed to delete contact: ${error.message || 'Unknown error'}. Check console for details.`)
      setDeleteConfirm({ show: false, contactId: null, contactName: '', isBulk: false, bulkCount: 0 })
    }
  }

  const cancelDelete = () => {
    console.log('Delete cancelled by user')
    setDeleteConfirm({ show: false, contactId: null, contactName: '', isBulk: false, bulkCount: 0 })
  }

  const handleSelectContact = (contactId) => {
    const newSelected = new Set(selectedContacts)
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId)
    } else {
      newSelected.add(contactId)
    }
    setSelectedContacts(newSelected)
    setShowBulkActions(newSelected.size > 0)
  }

  // Update ref whenever selectedContacts changes
  useEffect(() => {
    selectedContactsRef.current = selectedContacts
  }, [selectedContacts])
  
  // Save column widths to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('contacts-column-widths', JSON.stringify(columnWidths))
  }, [columnWidths])
  
  // Handle column resizing
  const handleResizeStart = (e, column) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startWidth = columnWidths[column]
    setResizing({ active: true, column, startX, startWidth })
    
    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX
      const newWidth = Math.max(50, startWidth + diff) // Minimum width of 50px
      setColumnWidths(prev => ({ ...prev, [column]: newWidth }))
    }
    
    const handleMouseUp = () => {
      setResizing({ active: false, column: null, startX: 0, startWidth: 0 })
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Update showBulkActions when selection or filtered contacts change
  useEffect(() => {
    setShowBulkActions(selectedContacts.size > 0)
  }, [selectedContacts.size])

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(sortedContacts.map(c => c.id))
      const newSelected = new Set(selectedContacts)
      sortedContacts.forEach(c => newSelected.add(c.id))
      setSelectedContacts(newSelected)
      setShowBulkActions(newSelected.size > 0)
    } else {
      // Only deselect currently visible contacts
      const newSelected = new Set(selectedContacts)
      sortedContacts.forEach(c => newSelected.delete(c.id))
      setSelectedContacts(newSelected)
      setShowBulkActions(newSelected.size > 0)
    }
  }

  const handleBulkTag = (productTag) => {
    // Use ref to get the latest selectedContacts to avoid stale closure issues
    const currentSelected = selectedContactsRef.current
    
    if (currentSelected.size === 0) {
      alert('Please select at least one contact to tag.')
      return
    }

    const selectedCount = currentSelected.size
    
    // Show confirmation modal
    setTagConfirm({
      show: true,
      tagName: productTag,
      contactCount: selectedCount
    })
  }

  const confirmBulkTag = async () => {
    const { tagName, contactCount } = tagConfirm
    const currentSelected = selectedContactsRef.current
    const contactIds = Array.from(currentSelected)

    try {
      const updatePromises = contactIds.map(async (contactId) => {
        const contact = contacts.find(c => c.id === contactId)
        if (!contact) return

        const currentTags = contact.tags || []
        
        // Only add tag if it doesn't already exist
        if (!currentTags.includes(tagName)) {
          const updatedTags = [...currentTags, tagName]
          await updateContact(contactId, { tags: updatedTags })
        }
      })

      await Promise.all(updatePromises)
      
      // Clear selection after tagging
      setSelectedContacts(new Set())
      setShowBulkActions(false)
      setTagConfirm({ show: false, tagName: '', contactCount: 0 })
      
      alert(`Successfully tagged ${contactCount} contact(s) with "${tagName}"`)
    } catch (error) {
      console.error('Error bulk tagging contacts:', error)
      alert(`Failed to tag contacts: ${error.message || 'Unknown error'}. Please try again.`)
      setTagConfirm({ show: false, tagName: '', contactCount: 0 })
    }
  }

  const cancelBulkTag = () => {
    setTagConfirm({ show: false, tagName: '', contactCount: 0 })
  }

  const handleBulkDelete = () => {
    if (selectedContacts.size === 0) return

    // Show custom confirmation modal for bulk delete
    setDeleteConfirm({ 
      show: true, 
      contactId: null, 
      contactName: '', 
      isBulk: true, 
      bulkCount: selectedContacts.size 
    })
  }

  const confirmBulkDelete = async () => {
    const count = deleteConfirm.bulkCount
    const contactIds = Array.from(selectedContacts)

    console.log('User confirmed bulk deletion, proceeding...')

    if (!user || !user.uid) {
      console.error('User not authenticated')
      alert('User not authenticated. Please refresh the page.')
      setDeleteConfirm({ show: false, contactId: null, contactName: '', isBulk: false, bulkCount: 0 })
      return
    }

    try {
      console.log('Calling deleteContact for', count, 'contacts')
      const deletePromises = contactIds.map(contactId => 
        deleteContact(contactId, user.uid)
      )
      await Promise.all(deletePromises)
      
      // Clear selection after deletion
      setSelectedContacts(new Set())
      setShowBulkActions(false)
      setDeleteConfirm({ show: false, contactId: null, contactName: '', isBulk: false, bulkCount: 0 })
      
      alert(`✅ Successfully deleted ${count} contact(s)`)
    } catch (error) {
      console.error('Error bulk deleting contacts:', error)
      alert(`❌ Failed to delete contacts: ${error.message || 'Unknown error'}. Check console for details.`)
      setDeleteConfirm({ show: false, contactId: null, contactName: '', isBulk: false, bulkCount: 0 })
    }
  }

  const handleRemoveTag = async (contactId, tagToRemove) => {
    try {
      const contact = contacts.find(c => c.id === contactId)
      if (!contact) return

      const currentTags = contact.tags || []
      const updatedTags = currentTags.filter(tag => tag !== tagToRemove)
      await updateContact(contactId, { tags: updatedTags })
    } catch (error) {
      console.error('Error removing tag:', error)
      alert('Failed to remove tag. Please try again.')
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return '-'
    
    // Handle Firestore Timestamp objects
    let date
    if (timestamp.toDate) {
      date = timestamp.toDate()
    } else if (timestamp instanceof Date) {
      date = timestamp
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000)
    } else {
      return '-'
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-'
    
    // Handle Firestore Timestamp objects
    let date
    if (timestamp.toDate) {
      date = timestamp.toDate()
    } else if (timestamp instanceof Date) {
      date = timestamp
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000)
    } else {
      return '-'
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getLinkedInStatusLabel = (status) => {
    const labels = {
      'not_started': 'Not Started',
      'initial_sent': 'Initial Sent',
      'followup_sent': 'Follow-up Sent',
      'replied': 'Replied',
      'value_offered': 'Value Offered',
      'converted': 'Converted'
    }
    return labels[status] || status
  }

  const getLinkedInStatusColor = (status) => {
    const colors = {
      'not_started': '#6b7280',
      'initial_sent': '#3b82f6',
      'followup_sent': '#8b5cf6',
      'replied': '#10b981',
      'value_offered': '#f59e0b',
      'converted': '#059669'
    }
    return colors[status] || '#6b7280'
  }

  const filteredContacts = contacts.filter((contact) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      contact.name?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.company?.toLowerCase().includes(searchLower) ||
      contact.phone?.toLowerCase().includes(searchLower)
    )
  })

  // Sort function
  const sortContacts = (contactsToSort, column, direction) => {
    if (!column) return contactsToSort

    return [...contactsToSort].sort((a, b) => {
      let aValue = a[column]
      let bValue = b[column]

      // Handle different data types
      if (column === 'createdAt' || column === 'updatedAt') {
        // Handle Firestore Timestamp objects
        const getDate = (timestamp) => {
          if (!timestamp) return new Date(0)
          if (timestamp.toDate) return timestamp.toDate()
          if (timestamp instanceof Date) return timestamp
          if (timestamp.seconds) return new Date(timestamp.seconds * 1000)
          return new Date(0)
        }
        aValue = getDate(aValue)
        bValue = getDate(bValue)
      } else if (column === 'tags') {
        // Sort by number of tags, then alphabetically by first tag
        const aTags = aValue || []
        const bTags = bValue || []
        if (aTags.length !== bTags.length) {
          return aTags.length - bTags.length
        }
        aValue = aTags.length > 0 ? aTags[0] : ''
        bValue = bTags.length > 0 ? bTags[0] : ''
      } else {
        // Handle text fields (name, email, company, phone, linkedinStatus)
        aValue = aValue || ''
        bValue = bValue || ''
        // Convert to string and lowercase for comparison
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
      }

      // Compare values
      let comparison = 0
      if (aValue < bValue) {
        comparison = -1
      } else if (aValue > bValue) {
        comparison = 1
      }

      return direction === 'asc' ? comparison : -comparison
    })
  }

  // Apply sorting to filtered contacts
  const sortedContacts = sortContacts(filteredContacts, sortColumn, sortDirection)

  // Handle column header click
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new column and default to ascending
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Get selected contacts that are currently visible
  const visibleSelectedContacts = sortedContacts.filter(c => selectedContacts.has(c.id))
  const visibleSelectedCount = visibleSelectedContacts.length

  if (loading) {
    return <div className="loading">Loading contacts...</div>
  }

  return (
    <div className="contacts-page">
      <div className="page-header">
        <div>
          <h2>Contacts</h2>
          <p className="subtitle">{contacts.length} total contacts</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => setShowBulkImport(true)}>
            Bulk Import
          </button>
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + New Contact
          </button>
        </div>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search contacts by name, email, company, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {showForm && (
        <ContactForm
          onSubmit={handleCreateContact}
          onCancel={() => setShowForm(false)}
        />
      )}

      {showBulkImport && (
        <BulkImportModal
          onClose={() => setShowBulkImport(false)}
          userId={user.uid}
          existingContacts={contacts}
        />
      )}

      {showBulkActions && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <strong>{selectedContacts.size}</strong> contact{selectedContacts.size !== 1 ? 's' : ''} selected
            {visibleSelectedCount < selectedContacts.size && (
              <span className="bulk-actions-note"> ({visibleSelectedCount} visible)</span>
            )}
          </div>
          <div className="bulk-actions-buttons">
            <div className="bulk-tag-dropdown">
              <select
                className="bulk-tag-select"
                value={selectedTagValue}
                onChange={async (e) => {
                  const selectedTag = e.target.value
                  console.log('Tag dropdown changed:', selectedTag)
                  console.log('Current selectedContacts:', Array.from(selectedContacts))
                  console.log('selectedContacts.size:', selectedContacts.size)
                  if (selectedTag) {
                    // Use a small delay to ensure state is captured
                    await new Promise(resolve => setTimeout(resolve, 0))
                    handleBulkTag(selectedTag)
                    // Reset dropdown
                    setSelectedTagValue('')
                  }
                }}
              >
                <option value="">Tag with...</option>
                {tags.length > 0 ? (
                  tags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))
                ) : (
                  <option value="" disabled>No tags available. Create tags in Tag Manager.</option>
                )}
              </select>
            </div>
            <button
              type="button"
              className="btn-danger"
              onClick={handleBulkDelete}
            >
              Delete Selected
            </button>
            <button
              className="btn-secondary"
              onClick={() => {
                setSelectedContacts(new Set())
                setShowBulkActions(false)
              }}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {sortedContacts.length === 0 ? (
        <div className="empty-state">
          <p>{searchTerm ? 'No contacts match your search.' : 'No contacts yet. Create your first contact!'}</p>
        </div>
      ) : (
        <div className="contacts-table-container">
          <table className="contacts-table" ref={tableRef}>
            <thead>
              <tr>
                <th 
                  className="select-column" 
                  style={{ width: `${columnWidths.select}px`, minWidth: `${columnWidths.select}px` }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={sortedContacts.length > 0 && sortedContacts.every(c => selectedContacts.has(c.id))}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleSelectAll(e.target.checked)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    title="Select all"
                  />
                </th>
                <th 
                  className="sortable resizable-column" 
                  style={{ width: `${columnWidths.name}px`, minWidth: `${columnWidths.name}px` }}
                  onClick={() => handleSort('name')}
                  title="Click to sort"
                >
                  <span>Name</span>
                  {sortColumn === 'name' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'name')}
                    title="Drag to resize"
                  />
                </th>
                <th 
                  className="sortable resizable-column" 
                  style={{ width: `${columnWidths.company}px`, minWidth: `${columnWidths.company}px` }}
                  onClick={() => handleSort('company')}
                  title="Click to sort"
                >
                  <span>Company</span>
                  {sortColumn === 'company' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'company')}
                    title="Drag to resize"
                  />
                </th>
                <th 
                  className="sortable resizable-column" 
                  style={{ width: `${columnWidths.email}px`, minWidth: `${columnWidths.email}px` }}
                  onClick={() => handleSort('email')}
                  title="Click to sort"
                >
                  <span>Email</span>
                  {sortColumn === 'email' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'email')}
                    title="Drag to resize"
                  />
                </th>
                <th 
                  className="sortable resizable-column" 
                  style={{ width: `${columnWidths.phone}px`, minWidth: `${columnWidths.phone}px` }}
                  onClick={() => handleSort('phone')}
                  title="Click to sort"
                >
                  <span>Phone</span>
                  {sortColumn === 'phone' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'phone')}
                    title="Drag to resize"
                  />
                </th>
                <th 
                  className="sortable resizable-column" 
                  style={{ width: `${columnWidths.tags}px`, minWidth: `${columnWidths.tags}px` }}
                  onClick={() => handleSort('tags')}
                  title="Click to sort"
                >
                  <span>Tags</span>
                  {sortColumn === 'tags' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'tags')}
                    title="Drag to resize"
                  />
                </th>
                <th 
                  className="sortable resizable-column" 
                  style={{ width: `${columnWidths.linkedinStatus}px`, minWidth: `${columnWidths.linkedinStatus}px` }}
                  onClick={() => handleSort('linkedinStatus')}
                  title="Click to sort"
                >
                  <span>LinkedIn Status</span>
                  {sortColumn === 'linkedinStatus' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'linkedinStatus')}
                    title="Drag to resize"
                  />
                </th>
                <th 
                  className="sortable resizable-column" 
                  style={{ width: `${columnWidths.createdAt}px`, minWidth: `${columnWidths.createdAt}px` }}
                  onClick={() => handleSort('createdAt')}
                  title="Click to sort"
                >
                  <span>Created</span>
                  {sortColumn === 'createdAt' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'createdAt')}
                    title="Drag to resize"
                  />
                </th>
                <th 
                  className="sortable resizable-column" 
                  style={{ width: `${columnWidths.updatedAt}px`, minWidth: `${columnWidths.updatedAt}px` }}
                  onClick={() => handleSort('updatedAt')}
                  title="Click to sort"
                >
                  <span>Last Updated</span>
                  {sortColumn === 'updatedAt' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'updatedAt')}
                    title="Drag to resize"
                  />
                </th>
                <th 
                  className="actions-column resizable-column" 
                  style={{ width: `${columnWidths.actions}px`, minWidth: `${columnWidths.actions}px` }}
                >
                  <span>Actions</span>
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, 'actions')}
                    title="Drag to resize"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedContacts.map((contact) => (
                <tr key={contact.id} className={`contact-row ${selectedContacts.has(contact.id) ? 'selected' : ''}`}>
                  <td 
                    className="select-column" 
                    style={{ width: `${columnWidths.select}px` }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedContacts.has(contact.id)}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleSelectContact(contact.id)
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td 
                    className="contact-name-cell"
                    style={{ width: `${columnWidths.name}px` }}
                  >
                    <div className="contact-name-wrapper">
                      <div className="contact-avatar">
                        {contact.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <Link to={`/contacts/${contact.id}`} className="contact-name">
                        {contact.name || 'Unnamed Contact'}
                      </Link>
                    </div>
                  </td>
                  <td 
                    className="contact-company-cell"
                    style={{ width: `${columnWidths.company}px` }}
                  >
                    {contact.company || '-'}
                  </td>
                  <td 
                    className="contact-email-cell"
                    style={{ width: `${columnWidths.email}px` }}
                  >
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td 
                    className="contact-phone-cell"
                    style={{ width: `${columnWidths.phone}px` }}
                  >
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td 
                    className="contact-tags-cell"
                    style={{ width: `${columnWidths.tags}px` }}
                  >
                    {contact.tags && contact.tags.length > 0 ? (
                      <div className="contact-tags">
                        {contact.tags.map((tag, idx) => (
                          <span key={idx} className="tag">
                            {tag}
                            <button
                              className="tag-remove"
                              onClick={() => handleRemoveTag(contact.id, tag)}
                              title="Remove tag"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td 
                    className="contact-linkedin-status-cell"
                    style={{ width: `${columnWidths.linkedinStatus}px` }}
                  >
                    {contact.linkedinStatus ? (
                      <span className="linkedin-status-badge" style={{
                        backgroundColor: getLinkedInStatusColor(contact.linkedinStatus) + '20',
                        color: getLinkedInStatusColor(contact.linkedinStatus)
                      }}>
                        {getLinkedInStatusLabel(contact.linkedinStatus)}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td 
                    className="contact-created-cell" 
                    style={{ width: `${columnWidths.createdAt}px` }}
                    title={formatDateTime(contact.createdAt)}
                  >
                    {formatDate(contact.createdAt)}
                  </td>
                  <td 
                    className="contact-updated-cell" 
                    style={{ width: `${columnWidths.updatedAt}px` }}
                    title={formatDateTime(contact.updatedAt)}
                  >
                    {formatDate(contact.updatedAt)}
                  </td>
                  <td 
                    className="contact-actions-cell"
                    style={{ width: `${columnWidths.actions}px` }}
                  >
                    <button
                      type="button"
                      className="delete-btn"
                      onClick={(e) => {
                        console.log('Button clicked directly!', contact.id)
                        e.stopPropagation()
                        handleDeleteContact(contact.id, e)
                      }}
                      onMouseDown={(e) => {
                        console.log('Button mousedown!', contact.id)
                        e.stopPropagation()
                      }}
                      title="Delete contact"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm.show && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <h3>⚠️ Delete {deleteConfirm.isBulk ? 'Contacts' : 'Contact'}</h3>
            {deleteConfirm.isBulk ? (
              <>
                <p>Are you sure you want to permanently delete <strong>{deleteConfirm.bulkCount} selected contact(s)</strong>?</p>
                <p className="warning-text">This action cannot be undone. All associated activities will also be deleted.</p>
              </>
            ) : (
              <>
                <p>Are you sure you want to permanently delete <strong>{deleteConfirm.contactName}</strong>?</p>
                <p className="warning-text">This action cannot be undone. All associated activities will also be deleted.</p>
              </>
            )}
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
              >
                {deleteConfirm.isBulk ? `Delete ${deleteConfirm.bulkCount} Contact(s)` : 'Delete Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tagConfirm.show && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <h3>🏷️ Apply Tag</h3>
            <p>Add the tag <strong>"{tagConfirm.tagName}"</strong> to <strong>{tagConfirm.contactCount} selected contact(s)</strong>?</p>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={cancelBulkTag}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={confirmBulkTag}
              >
                Apply Tag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Contacts

